import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Camera, RefreshCw, Plus, Euro, Image, Printer, Star,
  Check, Clock, AlertCircle, Download, ChevronDown, ChevronUp,
  ExternalLink, QrCode, Edit2, Save, Zap, TrendingUp, BarChart2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUT_CONFIG = {
  en_attente: { label: 'En attente',    color: '#9CA3AF', bg: 'rgba(156,163,175,0.1)' },
  en_cours:   { label: 'En cours',      color: 'var(--admin-blue)', bg: 'rgba(125,177,255,0.1)' },
  termine:    { label: 'Terminé',       color: 'var(--admin-amber)', bg: 'rgba(197,139,25,0.1)' },
  facture:    { label: 'Facturé',       color: 'var(--admin-green)', bg: 'rgba(31,157,122,0.1)' },
  solde:      { label: 'Soldé ✓',       color: 'var(--admin-gold)', bg: 'rgba(212,175,55,0.12)' },
};

// Simulateur de tarification
function SimulateurPrix() {
  const [nb, setNb] = useState(150);
  const [result, setResult] = useState(null);

  const simulate = useCallback(async () => {
    const res = await fetch(`${BACKEND}/api/photobooth/simuler`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nb_impressions: nb })
    });
    const d = await res.json();
    setResult(d);
  }, [nb]);

  useEffect(() => { simulate(); }, [simulate]);

  return (
    <div className="p-5 rounded-2xl space-y-4" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.2)' }}>
      <h3 className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>💰 Simulateur de tarification</h3>
      <div className="flex items-center gap-3">
        <Input
          type="number" min="0" max="2000" value={nb}
          onChange={e => setNb(parseInt(e.target.value) || 0)}
          style={{ background: 'var(--admin-surface2)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)', width: 120 }}
        />
        <span className="text-sm" style={{ color: 'var(--admin-muted)' }}>impressions</span>
      </div>
      {result && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Forfait', value: `${result.forfait_ttc}€`, color: 'var(--admin-blue)' },
              { label: 'Impressions', value: `${result.impressions_ttc}€`, color: 'var(--admin-amber)' },
              { label: 'Total TTC', value: `${result.total_ttc}€`, color: 'var(--admin-gold)' },
            ].map((k, i) => (
              <div key={i} className="p-2 rounded-lg text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-xs mb-0.5" style={{ color: 'var(--admin-muted)' }}>{k.label}</p>
                <p className="font-bold" style={{ color: k.color }}>{k.value}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1">
            {(result.tranches_detail || []).map((t, i) => (
              <div key={i} className="flex justify-between text-xs" style={{ color: 'var(--admin-muted)' }}>
                <span>Tranche {t.tranche} ({t.nb} × {t.pu_ttc}€)</span>
                <span style={{ color: 'var(--admin-gold)' }}>{t.sous_total_ttc}€</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'var(--admin-muted)' }}>
            <span>Coût papier ({nb} × 0.12€)</span>
            <span style={{ color: 'var(--admin-red)' }}>{result.cout_papier}€</span>
          </div>
          <div className="flex justify-between text-xs font-bold" style={{ color: 'var(--admin-green)' }}>
            <span>Marge brute</span>
            <span>{result.marge_brute}€</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal création événement
function CreateEventModal({ onSave, onClose }) {
  const [form, setForm] = useState({ nom_evenement: '', client_nom: '', client_email: '', date_evenement: '', type_photobooth: 'Photobooth', quote_id: '' });
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');

  async function submit() {
    if (!form.nom_evenement) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/photobooth/evenements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, forfait_paye: true })
      });
      const d = await res.json();
      toast.success('Événement créé !');
      onSave(d);
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-2xl p-5 space-y-4" style={{ background: 'var(--admin-bg)', border: '1px solid rgba(212,175,55,0.3)' }}>
        <h2 className="font-bold text-lg" style={{ color: 'var(--admin-gold)' }}>📸 Créer un événement photo</h2>
        {[
          { key: 'nom_evenement', label: 'Nom de l\'événement *', placeholder: 'Mariage Dupont - Juin 2026' },
          { key: 'client_nom', label: 'Client', placeholder: 'Jean Dupont' },
          { key: 'client_email', label: 'Email client', placeholder: 'client@email.fr', type: 'email' },
          { key: 'date_evenement', label: 'Date', type: 'date' },
          { key: 'quote_id', label: 'N° devis associé (optionnel)', placeholder: 'DEV-2026...' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>{f.label}</label>
            <Input type={f.type || 'text'} placeholder={f.placeholder || ''} value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
          </div>
        ))}
        <div>
          <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Type de Photobooth</label>
          <select value={form.type_photobooth} onChange={e => setForm(p => ({ ...p, type_photobooth: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
            {['Photobooth','Photobooth 360','Photobooth Rotatif 360'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex gap-2 pt-2">
          <Button onClick={submit} disabled={saving} style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)', flex: 1 }}>
            {saving ? 'Création...' : 'Créer l\'événement'}
          </Button>
          <Button onClick={onClose} variant="outline" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>Annuler</Button>
        </div>
      </div>
    </div>
  );
}

// Card événement
function EventCard({ ev, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editImp, setEditImp] = useState(false);
  const [nbImp, setNbImp] = useState(ev.nb_impressions || 0);
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const cfg = STATUT_CONFIG[ev.statut] || STATUT_CONFIG.en_attente;

  async function saveImpressions() {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/photobooth/evenements/${ev.id}/impressions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nb_impressions: parseInt(nbImp), source: 'admin' })
      });
      if (!res.ok) throw new Error();
      toast.success('Impressions mises à jour');
      setEditImp(false);
      onUpdate();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  async function createPayment() {
    try {
      const res = await fetch(`${BACKEND}/api/photobooth/evenements/${ev.id}/paiement-complement`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      const d = await res.json();
      if (d.url) window.open(d.url, '_blank');
      else toast.error(d.detail || 'Erreur Stripe');
    } catch { toast.error('Erreur'); }
  }

  async function markPaid() {
    await fetch(`${BACKEND}/api/photobooth/evenements/${ev.id}/complement-paye`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Marqué comme payé');
    onUpdate();
  }

  const calcul = ev.calcul;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--admin-surface)', border: `1px solid ${cfg.color}20` }}>
      {/* Header card */}
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--admin-gold-dim)' }}>
          <Camera size={18} style={{ color: 'var(--admin-gold)' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>{ev.nom_evenement}</p>
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
          </div>
          <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>
            {ev.client_nom} · {ev.date_evenement ? formatDate(ev.date_evenement) : 'Date ?'}
            {ev.nb_impressions > 0 && ` · ${ev.nb_impressions} impressions`}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          {calcul ? (
            <>
              <p className="font-bold" style={{ color: 'var(--admin-gold)' }}>{calcul.total_ttc}€</p>
              <p className="text-xs" style={{ color: ev.complement_paye ? '#1F9D7A' : '#C58B19' }}>
                {ev.complement_paye ? '✅ Soldé' : `+${calcul.complement_ttc}€ à encaisser`}
              </p>
            </>
          ) : (
            <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>Impressions non saisies</p>
          )}
        </div>
        {expanded ? <ChevronUp size={16} style={{ color: 'var(--admin-faint)' }} /> : <ChevronDown size={16} style={{ color: 'var(--admin-faint)' }} />}
      </div>

      {/* Détail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>

          {/* Compteur impressions */}
          <div className="pt-3">
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--admin-muted)' }}>COMPTEUR D'IMPRESSIONS</p>
            <div className="flex items-center gap-3">
              {editImp ? (
                <>
                  <Input type="number" min="0" value={nbImp} onChange={e => setNbImp(e.target.value)}
                    style={{ background: 'var(--admin-surface2)', borderColor: 'rgba(212,175,55,0.3)', color: 'var(--admin-text)', width: 120 }} />
                  <Button onClick={saveImpressions} disabled={saving} size="sm"
                    style={{ background: 'rgba(31,157,122,0.2)', color: 'var(--admin-green)', border: '1px solid rgba(31,157,122,0.3)' }}>
                    <Save size={13} className="mr-1" /> Sauvegarder
                  </Button>
                  <Button onClick={() => setEditImp(false)} variant="outline" size="sm"
                    style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>Annuler</Button>
                </>
              ) : (
                <>
                  <span className="adm-title"  style={{ color: 'var(--admin-gold)' }}>{ev.nb_impressions || 0}</span>
                  <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>impressions</span>
                  <Button onClick={() => setEditImp(true)} size="sm" variant="outline"
                    style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>
                    <Edit2 size={12} className="mr-1" /> Modifier
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Détail calcul */}
          {calcul && (
            <div className="p-3 rounded-xl space-y-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>DÉTAIL FACTURATION</p>
              <div className="space-y-1">
                <div className="flex justify-between text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <span>Forfait mise à disposition</span>
                  <span style={{ color: 'var(--admin-blue)' }}>{calcul.forfait_ttc}€ TTC</span>
                </div>
                {(calcul.tranches_detail || []).map((t, i) => (
                  <div key={i} className="flex justify-between text-xs" style={{ color: 'var(--admin-muted)' }}>
                    <span>Tranche {t.tranche} : {t.nb} × {t.pu_ttc}€</span>
                    <span style={{ color: 'var(--admin-amber)' }}>{t.sous_total_ttc}€</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'var(--admin-gold)' }}>
                  <span>TOTAL TTC</span>
                  <span>{calcul.total_ttc}€</span>
                </div>
                <div className="flex justify-between text-xs" style={{ color: 'var(--admin-faint)' }}>
                  <span>Coût papier</span>
                  <span style={{ color: 'var(--admin-red)' }}>-{calcul.cout_papier}€</span>
                </div>
                <div className="flex justify-between text-xs font-semibold" style={{ color: 'var(--admin-green)' }}>
                  <span>Marge brute</span>
                  <span>+{calcul.marge_brute}€</span>
                </div>
              </div>
            </div>
          )}

          {/* Galerie + QR */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>GALERIE CLIENT</p>
              {ev.gallery_url && (
                <a href={ev.gallery_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'var(--admin-teal)' }}>
                  <ExternalLink size={12} /> Ouvrir la galerie
                </a>
              )}
              {ev.qr_code_url && (
                <div className="mt-2">
                  <img src={ev.qr_code_url} alt="QR" className="w-20 h-20 rounded-lg" />
                </div>
              )}
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <p className="text-xs mb-2" style={{ color: 'var(--admin-muted)' }}>PHOTOS</p>
              <p className="adm-title"  style={{ color: 'var(--admin-blue)' }}>{ev.nb_photos_prises || 0}</p>
              <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>photos prises</p>
              <p className="text-sm font-bold mt-1" style={{ color: 'var(--admin-gold)' }}>{ev.nb_impressions || 0}</p>
              <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>impressions</p>
            </div>
          </div>

          {/* Actions paiement */}
          {calcul && calcul.complement_ttc > 0 && !ev.complement_paye && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={createPayment} size="sm"
                style={{ background: 'rgba(31,157,122,0.2)', color: 'var(--admin-green)', border: '1px solid rgba(31,157,122,0.3)' }}>
                <Zap size={13} className="mr-1" /> Générer lien Stripe ({calcul.complement_ttc}€)
              </Button>
              <Button onClick={markPaid} size="sm" variant="outline"
                style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-muted)' }}>
                <Check size={13} className="mr-1" /> Marquer payé manuellement
              </Button>
            </div>
          )}
          {ev.complement_paye && (
            <div className="flex items-center gap-2 p-2 rounded-xl" style={{ background: 'var(--admin-green-dim)', border: '1px solid rgba(31,157,122,0.2)' }}>
              <Check size={14} style={{ color: 'var(--admin-green)' }} />
              <p className="text-xs font-semibold" style={{ color: 'var(--admin-green)' }}>Complément payé — événement soldé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminPhotoboothV2() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, stRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/photobooth/evenements`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/photobooth/stats`, { headers: H }).then(r => r.json()),
      ]);
      setEvents(evRes.events || []);
      setStats(stRes);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'events', label: 'Événements', icon: <Camera size={14} /> },
    { id: 'simulateur', label: 'Simulateur', icon: <BarChart2 size={14} /> },
    { id: 'tarifs', label: 'Grille tarifaire', icon: <TrendingUp size={14} /> },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Photobooth V2</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Gestion des événements photobooth</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-photobooth-v2">
      {/* Header */}
      <div className="flex items-center justify-end flex-wrap gap-3">
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          <Button onClick={() => setShowCreate(true)} size="sm"
            style={{ background: 'rgba(212,175,55,0.2)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Plus size={14} className="mr-1" /> Nouvel événement
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Événements', value: stats.total_events, color: 'var(--admin-blue)' },
            { label: 'CA Forfaits', value: `${stats.ca_forfaits}€`, color: 'var(--admin-gold)' },
            { label: 'CA Impressions', value: `${stats.ca_complement}€`, color: 'var(--admin-amber)' },
            { label: 'En attente paiement', value: stats.en_attente_paiement, color: 'var(--admin-red)' },
          ].map((s, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${s.color}20` }}>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
              <p className="adm-title"  style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Bandeau tarifaire */}
      <div className="p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.1), rgba(212,175,55,0.03))', border: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--admin-gold)' }}>Forfait mise à disposition</p>
            <p className="adm-title"  style={{ color: 'var(--admin-text)' }}>125€ HT <span style={{ color: 'var(--admin-muted)', fontSize: 13 }}>/ 150€ TTC</span></p>
          </div>
          <div style={{ width: 1, height: 40, background: 'rgba(255,255,255,0.1)' }} />
          {[
            { label: '1–200 impressions', pu: '0.70€' },
            { label: '201–400 impressions', pu: '0.55€' },
            { label: '401+ impressions', pu: '0.40€' },
          ].map((t, i) => (
            <div key={i}>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{t.label}</p>
              <p className="font-bold" style={{ color: 'var(--admin-gold)' }}>{t.pu} <span style={{ color: 'var(--admin-faint)', fontSize: 10 }}>/impression</span></p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={activeTab === tab.id
              ? { background: '#fff', color: 'var(--admin-teal)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }
              : { color: 'var(--admin-muted)' }}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {/* Événements */}
      {activeTab === 'events' && (
        <div className="space-y-3">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 rounded-2xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
          ) : events.length === 0 ? (
            <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}>
              <Camera size={40} className="mx-auto mb-3 opacity-20" />
              <p>Aucun événement photobooth pour le moment</p>
            </div>
          ) : events.map(ev => (
            <EventCard key={ev.id} ev={ev} onUpdate={load} />
          ))}
        </div>
      )}

      {/* Simulateur */}
      {activeTab === 'simulateur' && <SimulateurPrix />}

      {/* Grille tarifaire */}
      {activeTab === 'tarifs' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="p-5 rounded-2xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
            <h3 className="font-bold mb-4" style={{ color: 'var(--admin-gold)' }}>Grille tarifaire détaillée</h3>
            <div className="space-y-2">
              {[50,100,150,200,250,300,400,500,750,1000].map(n => {
                const cost_ttc_imp = n <= 200 ? n * 0.70 : n <= 400 ? 200 * 0.70 + (n-200) * 0.55 : 200 * 0.70 + 200 * 0.55 + (n-400) * 0.40;
                const total = 150 + cost_ttc_imp;
                return (
                  <div key={n} className="flex items-center gap-4 py-2 text-sm" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <span className="w-32" style={{ color: 'var(--admin-gold)', fontWeight: 700 }}>{n} impressions</span>
                    <span style={{ color: 'var(--admin-muted)' }}>{cost_ttc_imp.toFixed(2)}€ impressions</span>
                    <span className="ml-auto font-bold" style={{ color: 'var(--admin-text)' }}>{total.toFixed(2)}€ TTC total</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="p-4 rounded-2xl" style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.2)' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--admin-green)' }}>Message client</p>
            {['Payez uniquement ce que vous photographiez.', 'Vous ne payez que ce que vous consommez.', 'Aucune perte d\'impressions inutilisées.'].map((m, i) => (
              <p key={i} className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>• {m}</p>
            ))}
          </div>
        </div>
      )}

      {showCreate && <CreateEventModal onSave={() => { setShowCreate(false); load(); }} onClose={() => setShowCreate(false)} />}
      </div>
    </div>
  );
}
